/**
 * Owner-facing lab tracking on My Bookings. A read-only timeline of the fulfillment journey
 * (Confirmed → Sample collected → Processing → Report ready), a soft "report ready" moment, and the
 * report (notes + downloadable document) once ready. Self-gates to LAB bookings and only fetches
 * once the request has been accepted. Mirrors OwnerCremationTracker.
 */
import { Check, Loader2 } from 'lucide-react';
import { ImageCarousel } from '@/components/image-carousel';
import { use_owner_fulfillment } from '@/features/lab-fulfillment/hooks';
import {
  stage_label,
  LAB_STAGE_ORDER,
  LAB_STAGE_TIMESTAMP,
  type LabFulfillment,
} from '@/features/lab-fulfillment/types';
import type { Booking } from '@/features/booking/types';

function format_stamp(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Timeline({ f }: { f: LabFulfillment }) {
  const current_idx = LAB_STAGE_ORDER.indexOf(f.status);
  return (
    <ol className="flex flex-col" data-testid={`lab-timeline-${f.booking_id}`}>
      {LAB_STAGE_ORDER.map((stage, idx) => {
        const done = idx <= current_idx;
        const is_current = idx === current_idx;
        const stamp = format_stamp(f[LAB_STAGE_TIMESTAMP[stage]] as string | null);
        const last = idx === LAB_STAGE_ORDER.length - 1;
        return (
          <li key={stage} className="flex gap-3" data-testid={`lab-timeline-step-${stage}`}>
            <div className="flex flex-col items-center">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                  done ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card text-muted-foreground'
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : idx + 1}
              </span>
              {!last && <span className={`w-0.5 flex-1 ${idx < current_idx ? 'bg-primary' : 'bg-border'}`} />}
            </div>
            <div className={last ? '' : 'pb-4'}>
              <p className={`text-sm font-semibold ${is_current ? 'text-primary' : done ? 'text-foreground' : 'text-muted-foreground'}`}>
                {stage_label(stage)}
              </p>
              {stamp && <p className="text-xs text-muted-foreground">{stamp}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function OwnerLabTracker({ booking }: { booking: Booking }) {
  const not_started =
    booking.status === 'AWAITING_VENDOR' ||
    booking.status === 'PENDING_PAYMENT' ||
    booking.status === 'CANCELLED' ||
    booking.status === 'NO_SHOW';

  const f_q = use_owner_fulfillment(booking.id, booking.module_type === 'LAB' && !not_started);

  if (booking.module_type !== 'LAB') return null;

  if (not_started) {
    if (booking.status === 'CANCELLED' || booking.status === 'NO_SHOW') return null;
    return (
      <div
        className="basis-full rounded-card border-2 border-brand-outline-variant/20 bg-muted/30 p-3 text-sm text-muted-foreground"
        data-testid={`lab-awaiting-${booking.id}`}
      >
        We’ve shared your test request with the lab. We’ll keep you updated here once they confirm.
      </div>
    );
  }

  const f = f_q.data;

  return (
    <div className="basis-full" data-testid={`lab-track-${booking.id}`}>
      {f_q.isLoading ? (
        <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !f ? null : (
        <div className="flex flex-col gap-3 rounded-card border-2 border-brand-outline-variant/20 bg-card p-4 shadow-clay-sm">
          {f.status === 'REPORT_READY' && (
            <p
              className="rounded-input border-2 border-primary/25 bg-primary/[0.06] p-2.5 text-sm text-foreground"
              data-testid={`lab-report-ready-${booking.id}`}
            >
              Your report is ready to view below.
            </p>
          )}

          <Timeline f={f} />

          {f.status === 'REPORT_READY' && f.report_notes && (
            <div
              className="rounded-input border-2 border-brand-outline-variant/20 bg-muted/40 p-3"
              data-testid={`lab-report-notes-${booking.id}`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Report notes</p>
              <p className="mt-1 whitespace-pre-wrap text-sm">{f.report_notes}</p>
            </div>
          )}

          {/* Downloadable report document(s) — visible once ready. */}
          {f.report_documents.length > 0 && (
            <div data-testid={`lab-report-docs-${booking.id}`}>
              <ImageCarousel images={f.report_documents} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
